/**
 * Turns a connection-string parse result into a patch for the connection dialog's form.
 *
 * Overwrite semantics (spec §05): only the fields the string carried, plus `authentication`, which must be a valid
 * literal for the chosen engine (the carried signal wins, else the engine default) or the auth <select> would show a
 * stale value. Name, Color and SSH are never touched. The dialog applies the patch through its draft contract
 * (`useEngineDrafts.applyPatch`, Option C C0): a string for another engine switches to that engine's draft first, so the
 * draft the user was typing is kept rather than overwritten.
 */

import type { ConnectionFormValues, DatabaseEngine } from '../../components/connectionFormSchema';
import type { ConnectionStringParseResult } from './parseConnectionString';
import { defaultAuthForEngine } from './helpers';

export type ParseResultPatch = { engine: DatabaseEngine; patch: Partial<Omit<ConnectionFormValues, 'engine'>> };

export function parseResultPatch(result: ConnectionStringParseResult): ParseResultPatch | null {
  const { engine, values } = result;
  if (!engine) return null;
  // A string refused with a reason (C3 review r2, C3-11: a Kusto address it cannot read completely) fills nothing — not
  // even the engine's default authentication over the draft it lands on.
  if (result.problem) return { engine, patch: {} };

  const patch: Record<string, unknown> = {
    authentication: (values.authentication as string | undefined) ?? defaultAuthForEngine(engine),
  };
  for (const [key, value] of Object.entries(values)) {
    if (key === 'engine' || key === 'authentication' || value === undefined) continue;
    patch[key] = value;
  }
  if (engine === 'azurestorage' && (values.azureStorageScope !== undefined || values.azureStorageEmulator !== undefined)) {
    // S7-10: a new storage scope replaces any File / Folder choice left from an earlier paste (undefined = none needed).
    patch.azureStorageShareChoice = values.azureStorageShareChoice;
    // C3: a string that says which mode it is in decides "Connect using"; the user's earlier pick steps aside. A string
    // that says nothing about the mode (or one for another engine) leaves the pick in the storage draft.
    patch.azureStorageModePick = undefined;
  }
  return { engine, patch: patch as ParseResultPatch['patch'] };
}
